import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t, tKey } from 'ember-intl';

export const PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS = {
  STAY_DATE: 'stayDate',
  HOTEL: 'hotel',
} as const;
export type PortfolioSmartInsightsAggregationOption =
  (typeof PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS)[keyof typeof PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS];

const PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS_TRANSLATIONS = {
  [PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS.STAY_DATE]: tKey(
    'market.smart_insights.dropdown_aggregation.stay_date',
  ),
  [PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS.HOTEL]: tKey(
    'market.smart_insights.dropdown_aggregation.hotel',
  ),
};

type DropdownAggregationOption = Option & {
  id: PortfolioSmartInsightsAggregationOption;
};

export interface MarketSmartInsightsDropdownAggregationSignature {
  Args: {
    selectedAggregationOption: PortfolioSmartInsightsAggregationOption;
    setSelectedAggregationOption: (
      option: PortfolioSmartInsightsAggregationOption,
    ) => void;
  };
}

export default class MarketSmartInsightsDropdownAggregation extends Component<MarketSmartInsightsDropdownAggregationSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get options() {
    return Object.values(PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS).map(
      (option) => ({
        id: option,
        label: this.intl.t(
          PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS_TRANSLATIONS[option],
        ),
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOption() {
    return (
      this.options.find(
        (option) => option.id === this.args.selectedAggregationOption,
      ) ?? null
    );
  }

  @action onChange(option: DropdownAggregationOption): void {
    const { id } = option;

    this.args.setSelectedAggregationOption(id);

    this.usageAnalytics.track('Smart Insights Grouped', {
      eventTrigger: 'smart-insights-dropdown-grouping',
      product: 'smart-insights-multi-property',
      value: id,
    });
  }

  <template>
    <div class="flex items-center gap-200">
      <span class="text-300--regular">{{t
          "market.smart_insights.dropdown_aggregation.group_by"
        }}</span>

      <PrismSelectInput
        @onChange={{this.onChange}}
        @options={{this.options}}
        @value={{this.selectedOption}}
      />
    </div>
  </template>
}
